import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, X, RotateCcw, Sun, Moon } from 'lucide-react';
import { THEMES } from '../lib/theme';
import { useSiteContent } from '../context/ContentContext';
import { toast } from 'sonner';

const ThemePicker = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem('tf_theme') || 'site');
  const [mode, setMode] = useState(() => localStorage.getItem('tf_mode') || 'site');
  const { data } = useSiteContent();

  const sitePreset = data?.theme?.preset || 'emeraude';
  const siteMode = data?.theme?.mode || 'dark';

  const pick = (id) => {
    if (id === 'site') {
      localStorage.removeItem('tf_theme');
      setCurrent('site');
      toast.success('Thème du site restauré');
    } else {
      localStorage.setItem('tf_theme', id);
      setCurrent(id);
      toast.success(`Thème « ${THEMES.find((x) => x.id === id)?.label} » appliqué`);
    }
    window.dispatchEvent(new Event('tf-theme'));
  };

  const pickMode = (m) => {
    if (m === 'site') {
      localStorage.removeItem('tf_mode');
      setMode('site');
    } else {
      localStorage.setItem('tf_mode', m);
      setMode(m);
      toast.success(m === 'light' ? 'Mode clair activé' : 'Mode sombre activé');
    }
    window.dispatchEvent(new Event('tf-theme'));
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2.2, type: 'spring', stiffness: 200 }}
        onClick={() => setOpen(!open)}
        data-testid="theme-picker-trigger"
        aria-label="Changer de thème"
        className="clip-frame-sm fixed bottom-24 left-4 z-[84] flex h-11 w-11 items-center justify-center border border-white/15 bg-void/90 text-slate-300 backdrop-blur transition-all hover:border-neon hover:text-neon sm:bottom-6 sm:left-6"
      >
        {open ? <X size={17} /> : <Palette size={17} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="clip-frame fixed bottom-40 left-4 z-[84] w-72 border border-neon/25 bg-panel p-4 shadow-[0_0_50px_rgba(0,0,0,0.5)] sm:bottom-20 sm:left-6"
            data-testid="theme-picker-panel"
          >
            <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neon">
              <Palette size={12} /> Thème du laboratoire
            </p>
            <div className="space-y-1.5">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => pick(t.id)}
                  data-testid={`theme-option-${t.id}`}
                  className={`flex w-full items-center gap-3 border px-3 py-2.5 text-left transition-all ${
                    current === t.id ? 'border-neon/60 bg-neon/10' : 'border-white/8 hover:border-neon/30'
                  }`}
                >
                  <span className="flex -space-x-1">
                    {[t.neon, t.cyber, t.violet].map((c, i) => (
                      <span key={i} className="h-4 w-4 rounded-full border border-void" style={{ background: `rgb(${c.replace(/\s+/g, ',')})` }} />
                    ))}
                  </span>
                  <span className="flex-1">
                    <span className="block text-xs font-semibold text-slate-100">{t.label}</span>
                    <span className="block font-mono text-[8px] uppercase tracking-[0.15em] text-slate-500">{t.desc}</span>
                  </span>
                  {current === t.id && <span className="h-1.5 w-1.5 rounded-full bg-neon" />}
                </button>
              ))}
              <button
                onClick={() => pick('site')}
                data-testid="theme-option-site"
                className={`flex w-full items-center gap-2 border px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] transition-all ${
                  current === 'site' ? 'border-cyber/50 text-cyber' : 'border-white/8 text-slate-500 hover:text-slate-300'
                }`}
              >
                <RotateCcw size={11} /> Thème du site ({THEMES.find((t) => t.id === sitePreset)?.label})
              </button>
            </div>

            <p className="mb-2 mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-neon">Luminosité</p>
            <div className="flex gap-1.5" data-testid="mode-toggle">
              {[
                { id: 'light', label: 'Clair', icon: Sun },
                { id: 'dark', label: 'Sombre', icon: Moon },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => pickMode(m.id)}
                  data-testid={`mode-option-${m.id}`}
                  className={`flex flex-1 items-center justify-center gap-2 border px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-all ${
                    mode === m.id ? 'border-neon/60 bg-neon/10 text-neon' : 'border-white/8 text-slate-400 hover:border-neon/30'
                  }`}
                >
                  <m.icon size={13} /> {m.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => pickMode('site')}
              data-testid="mode-option-site"
              className={`mt-1.5 flex w-full items-center gap-2 border px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] transition-all ${
                mode === 'site' ? 'border-cyber/50 text-cyber' : 'border-white/8 text-slate-500 hover:text-slate-300'
              }`}
            >
              <RotateCcw size={11} /> Mode du site ({siteMode === 'light' ? 'clair' : 'sombre'})
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ThemePicker;
